/**
 * PartnerDigestSheet — create / edit a partner inventory digest.
 *
 * One digest = a partner's recurring restock alert: a label, a Slack channel
 * (Threadbot), a cadence (daily / weekly + weekday), and a chosen SKU list.
 * Only the SKUs at/below their threshold are surfaced when it fires.
 */
import { useEffect, useState } from 'react';
import { Loader2, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SlackChannelPicker } from '@/features/organization/components/SlackChannelPicker';
import { useLocation } from '@/contexts/LocationContext';
import { SkuMultiSelect } from './SkuMultiSelect';

/** Sentinel for the "All locations" option (Select can't take an empty value). */
const ALL_LOCATIONS = '__all__';

export type PartnerDigestDraft = {
  id: string;
  label: string;
  slackChannel: string | null;
  cadence: 'daily' | 'weekly';
  weeklyDay: number;
  enabled: boolean;
  locationId: string | null;
  skuIds: string[];
};

const WEEKDAYS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing digest to edit, or null to create a new one. */
  digest: PartnerDigestDraft | null;
}

export function PartnerDigestSheet({ open, onOpenChange, digest }: Props) {
  const utils = trpc.useUtils();
  const isEdit = !!digest;
  const { locations } = useLocation();

  const [label, setLabel] = useState('');
  const [slackChannel, setSlackChannel] = useState<string | null>(null);
  const [cadence, setCadence] = useState<'daily' | 'weekly'>('weekly');
  const [weeklyDay, setWeeklyDay] = useState(1);
  const [enabled, setEnabled] = useState(true);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [skuIds, setSkuIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setLabel(digest?.label ?? '');
    setSlackChannel(digest?.slackChannel ?? null);
    setCadence(digest?.cadence ?? 'weekly');
    setWeeklyDay(digest?.weeklyDay ?? 1);
    setEnabled(digest?.enabled ?? true);
    setLocationId(digest?.locationId ?? null);
    setSkuIds(digest?.skuIds ?? []);
  }, [open, digest]);

  const invalidate = () => utils.partnerDigests.list.invalidate();

  const createMut = trpc.partnerDigests.create.useMutation({
    onSuccess: () => {
      toast.success('Digest created');
      invalidate();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.message),
  });
  const updateMut = trpc.partnerDigests.update.useMutation({
    onSuccess: () => {
      toast.success('Digest saved');
      invalidate();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.message),
  });
  const deleteMut = trpc.partnerDigests.delete.useMutation({
    onSuccess: () => {
      toast.success('Digest deleted');
      invalidate();
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.message),
  });
  const testMut = trpc.partnerDigests.testSend.useMutation({
    onSuccess: (r) =>
      toast.success(
        r.atRisk > 0
          ? `Sent: ${r.atRisk} SKU${r.atRisk === 1 ? '' : 's'} at risk`
          : 'Sent: all SKUs healthy',
      ),
    onError: (e) => toast.error(e.message),
  });

  const saving = createMut.isPending || updateMut.isPending;
  const canSave = label.trim().length > 0;

  const handleSave = () => {
    const payload = {
      label: label.trim(),
      slackChannel,
      cadence,
      weeklyDay,
      enabled,
      locationId,
      skuIds,
    };
    if (isEdit && digest) updateMut.mutate({ id: digest.id, ...payload });
    else createMut.mutate(payload);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Edit partner digest' : 'New partner digest'}</SheetTitle>
          <SheetDescription>
            A recurring Slack update of the SKUs you choose, surfacing only those at or below
            their restock threshold.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <Label htmlFor="digest-label">Partner name</Label>
            <Input
              id="digest-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Heaps Good"
            />
          </div>

          <div className="space-y-2">
            <Label>Channel</Label>
            <SlackChannelPicker value={slackChannel} onChange={setSlackChannel} />
            <p className="text-xs text-muted-foreground">
              Posts via Threadbot; invite it to the shared channel first.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Frequency</Label>
            <div className="flex gap-2">
              <Select value={cadence} onValueChange={(v) => setCadence(v as 'daily' | 'weekly')}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                </SelectContent>
              </Select>
              {cadence === 'weekly' && (
                <Select value={String(weeklyDay)} onValueChange={(v) => setWeeklyDay(Number(v))}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Sends at 08:00 AEST.</p>
          </div>

          <div className="space-y-2">
            <Label>Location</Label>
            <Select
              value={locationId ?? ALL_LOCATIONS}
              onValueChange={(v) => setLocationId(v === ALL_LOCATIONS ? null : v)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_LOCATIONS}>All locations</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Limit the digest to one location's stock, or report across all of them.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Tracked SKUs</Label>
            <SkuMultiSelect value={skuIds} onChange={setSkuIds} />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="digest-enabled">Enabled</Label>
              <p className="text-xs text-muted-foreground">Turn off to pause without deleting.</p>
            </div>
            <Switch id="digest-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div className="flex items-center gap-2 pt-2 border-t">
            <Button onClick={handleSave} disabled={!canSave || saving} className="flex-1">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isEdit ? 'Save changes' : 'Create digest'}
            </Button>
            {isEdit && digest && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => testMut.mutate({ id: digest.id })}
                  disabled={testMut.isPending}
                  title="Post this digest to its channel right now"
                >
                  {testMut.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Send test
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                  onClick={() => deleteMut.mutate({ id: digest.id })}
                  disabled={deleteMut.isPending}
                  title="Delete digest"
                >
                  {deleteMut.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </Button>
              </>
            )}
          </div>
          {!isEdit && (
            <p className="text-xs text-muted-foreground">
              Create the digest first, then reopen it to send a test to the channel.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
